import { useEffect, useState } from "react";
import { createTheme, CssBaseline, ThemeProvider } from "@mui/material";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import MicRecorder from "mic-recorder-to-mp3";

import EndDialog from "./EndDialog";
import TrialStart from "./TrialDialog";
import IntroductionSlide from "./IntroductionSlide";
import TestProcessSlide from "./TestProcessSlide";
import QuestionSlide from "./QuestionSlide";
import TestWordSlide from "./TestWordSlide";
import ConfirmationDialog from "./ConfirmationDialog";

import { PlayFunction } from "use-sound/dist/types";
import { TestContext, TestParams, TestState } from "../../types/test-types";
import { initialWords, preDefinedWords } from "../../assets/words/words";
import { fromCollection } from "../../utils/word-utils";
import { downloadResultsFile, downloadVoiceRecordingFileMp3 } from "../../utils/file-utils";
import useTestTimer from "../../utils/hooks/useTestTimer";
import { initialState } from "../../utils/hooks/useTestReducer";
import useClickListener from "../../utils/hooks/useClickListener";
import MiddlePauseSlide from "./MiddlePauseSlide";

const whiteTheme = createTheme({
  shape: {
    borderRadius: 20,
  },
  palette: {
    background: {
      default: "rgb(245, 245, 245)",
    },
  },
});

const greyTheme = createTheme({
  shape: {
    borderRadius: 20,
  },
  palette: {
    background: {
      default: "rgba(0, 0, 0, 0.5)",
    },
  },
});

const styles = {
  fadeIn: {
    transition: "opacity 0.5s ease",
  },
  fadeOut: {
    opacity: 0,
    transition: "opacity 0.5s ease",
  },
  center: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
};

const initialTestContext = {
  showTrialModal: false,
  usedWords: [],
  startedDate: new Date(),
};

const recorder = new MicRecorder({
  bitrate: 128,
});

const TestProcess = (props: { state: TestState; trial: boolean; testing: boolean; play: PlayFunction }) => {
  const [testContext, setTestContext] = useState<TestContext>(initialTestContext);
  const [testParams, setTestParams] = useState<TestParams>(props.state.testParams);
  const [backConfirmation, setBackConfirmation] = useState(false);
  const [recording, setRecording] = useState<boolean>(false);

  const { variables, startTimer, toggleTimer } = useTestTimer(initialWords, testContext.usedWords, testParams);
  const setClickListenerState = useClickListener(toggleTimer);

  useEffect(() => {
    if (testParams.testSoundRecording && !props.testing) {
      if (variables.started && !recording) {
        setRecording(true);
        recorder.start();
      }
      if (variables.ended) {
        recorder
          .stop()
          .getMp3()
          .then(([buffer, blob]: [Array<Uint8Array>, Blob]) => {
            props.state.testParams.testSoundBuffer = buffer;
            props.state.testParams.testSoundBlob = blob;
          });
      }
    }
    if (variables.step === 4 || variables.ended || variables.step === 5 || variables.step === 6) {
      props.play();
    }
  }, [variables.started, variables.step, variables.ended]);

  useEffect(() => {
    setClickListenerState(variables.started && !variables.ended);
  }, [variables.started, variables.ended]);

  useEffect(() => {
    if (props.trial) {
      setTestContext({ ...testContext, showTrialModal: true, usedWords: preDefinedWords });
      setTestParams(initialState.testParams);
    } else {
      setTestContext({ ...testContext, usedWords: fromCollection() });
      startTimer();
    }

    if (props.testing) {
      setTestParams({
        ...initialState.testParams,
        testInterval: 0.005,
        testPauseDuration: 0.005,
        introductionSlideDuration: 0.005,
        testProcessSlideDuration: 0.005,
        questionsSlideDuration: 0.005,
        testSoundRecording: testParams.testSoundRecording,
      });
    }
  }, []);

  const mainComponent = () => {
    if (variables.step === 0) {
      return <IntroductionSlide />;
    } else if (variables.step === 1) {
      return <TestProcessSlide />;
    } else if (variables.step === 2) {
      return <QuestionSlide />;
    } else if (variables.step === 5) {
      return <MiddlePauseSlide variables={variables} />;
    }
    return <TestWordSlide variables={variables} />;
  };

  return (
    <ThemeProvider theme={variables.paused ? greyTheme : whiteTheme}>
      <CssBaseline />

      {mainComponent()}

      {variables.paused && (
        <PauseRoundedIcon
          sx={{
            fontSize: "10rem",
            color: "rgba(222, 222, 222, 0.9)",
            ...styles.center,
          }}
        />
      )}

      <TrialStart
        isOpen={testContext.showTrialModal}
        startTimer={() => {
          startTimer();
          setTestContext({ ...testContext, showTrialModal: false });
        }}
      />
      <EndDialog
        isOpen={variables.ended}
        downloadWords={() => downloadResultsFile(testContext.usedWords, props.state.userCode, testContext.startedDate)}
        navigateFunction={() => setBackConfirmation(true)}
        showRecording={testParams.testSoundRecording}
        downloadRecording={() =>
          downloadVoiceRecordingFileMp3(
            props.state.userCode,
            testContext.startedDate,
            props.state.testParams.testSoundBuffer as Array<Uint8Array>,
            props.state.testParams.testSoundBlob as Blob
          )
        }
      />
      <ConfirmationDialog isOpen={backConfirmation} cancelGoingBack={() => setBackConfirmation(false)} />
    </ThemeProvider>
  );
};

export default TestProcess;
